import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import imageCompression from "browser-image-compression";
import Sortable from "sortablejs";

// Connects to data-controller="image-upload-form"
export default class extends Controller {
  static targets = ["fileSelect"]

  // 型定義
  declare readonly fileSelectTarget: HTMLElement

  connect() {
    this.onFileRemove();
    this.enableDragAndDrop();
  }

  // 初期状態で画像プレビューがある場合の削除ボタンの処理
  onFileRemove() {
    const fileRemoveButtons = document.querySelectorAll('.btn_fileRemove');
    fileRemoveButtons.forEach(button => {
      button.addEventListener('click', function () {
        const parentElement = button.parentElement;
        if (parentElement) {
          const grandParentElement = parentElement.parentElement;
          if (grandParentElement) {
            const previousElement = grandParentElement.previousElementSibling;
            if (previousElement) {
              previousElement.classList.remove('is_max');
            }
          }
          parentElement.remove();
        }
      });
    });
  }

  // 画像の並び替え処理
  enableDragAndDrop() {
    const selectedImgs = document.querySelectorAll<HTMLUListElement>("ul.selectedImg");
    const elem = this.fileSelectTarget as HTMLElement;
    const fileSelectBtn = elem.parentNode as HTMLElement;

    selectedImgs.forEach((selectedImg) => {
      // すでにSortableが適用されている場合は処理をスキップ
      if (Sortable.get(selectedImg) !== undefined) return;

      Sortable.create(selectedImg, {
        filter: ".fileRemoveBtn",
        onFilter: (evt) => {
          // イベントのアイテム（削除ボタンの親のli要素）を取得
          const itemToRemove = evt.item.closest("li");
          // アイテムが存在する場合はDOMから削除
          if (itemToRemove && itemToRemove.parentNode) {
            itemToRemove.parentNode.removeChild(itemToRemove);
          }
          // 写真を選択ボタンを活性化
          fileSelectBtn.classList.remove("is_max");
        },
      });
    });
  }

  fileLimit(event) {
    const elem = this.fileSelectTarget as HTMLInputElement;
    const limit = Number(elem.dataset.limit); // アップロード可能なファイルの上限数
    const dataSizeLimit = Number(elem.dataset.threshold); // アップロード可能なファイルの最大容量
    // const selectedImg = (elem.parentNode as Element)?.nextElementSibling as HTMLElement; // プレビュー表示エリア
    const selectedImgs = document.getElementById('selectedImg') as HTMLUListElement; // プレビュー表示エリア
    const fileSelectBtn = elem.parentNode as HTMLElement; // 「写真を選択」ボタン
    const selectedNum = selectedImgs?.children.length || 0; // すでに選択済みで表示されている枚数
    let filesNum = elem.files?.length || 0; // 新しく選択した画像の枚数

    let loadStartCount = 0;  // ファイル読み込み開始カウンタ
    let loadEndCount = 0;    // ファイル読み込み完了カウンタ

    for (let i = 0; i < filesNum; i++) {
      // ファイル枚数超過時の警告
      if (limit < filesNum + selectedNum) {
        Modal.getOrCreateInstance(document.getElementById('alertModal_imageNumber')!).show();
        filesNum = 0;
        fileSelectBtn.classList.remove("is_max");
        return;
      } else if (limit === filesNum + selectedNum) {
        fileSelectBtn.classList.add("is_max");
      } else {
        fileSelectBtn.classList.remove("is_max");
      }

      // ファイルが指定サイズ以上だと警告とアイテム削除
      if (elem.files![i].size > dataSizeLimit) {
        Modal.getOrCreateInstance(document.getElementById('alertModal_imageSize')!).show();
        fileSelectBtn.classList.remove("is_max");
        return false;
      }
      // ファイルの圧縮・読み込み
      const fileReader = new FileReader();
      loadStartCount++;  // ファイル読み込み開始カウンタを増やす

      this.compressFile(elem.files![i], fileReader);

      // hiddenタグを生成
      const hiddenIdTag = this.createHiddenTag(`${event.params.prefix}_upload_images[id][]`, null);
      const hiddenImageTag = this.createHiddenTag(`${event.params.prefix}_upload_images[image][]`, null);
      const hiddenFileNameImageTag = this.createHiddenTag(`${event.params.prefix}_upload_images[filename][]`, elem.files![i].name);

      fileReader.onload = () => {
        loadEndCount++;  // ファイル読み込み完了カウンタを増やす

        if (limit >= filesNum) {
          // ファイル読み取りが完了後の処理
          const imgTag = document.createElement('li');
          imgTag.setAttribute('draggable', 'true');
          imgTag.innerHTML = `<img src='${fileReader.result}'><button type="button" class="btn btn_clAlert_outline btn_fileRemove"><i class="fa-solid fa-trash-can"></i>削除</button>`;

          selectedImgs.insertAdjacentElement("beforeend", imgTag);

          // 直前に追加した削除ボタンを取得
          const lastRemoveBtn = imgTag.querySelector('.btn_fileRemove') as HTMLElement;

          // 画像送信用 input を作成してプレビュー画像と削除ボタンの間に挿入
          hiddenImageTag.value = fileReader.result as string;
          imgTag.insertBefore(hiddenIdTag, lastRemoveBtn);
          imgTag.insertBefore(hiddenImageTag, lastRemoveBtn);
          imgTag.insertBefore(hiddenFileNameImageTag, lastRemoveBtn);

          // 削除ボタンで該当の写真削除
          lastRemoveBtn.addEventListener("click", function () {
            imgTag.remove();
            fileSelectBtn.classList.remove("is_max");
            if (selectedImgs.children.length === 0) {
              document.getElementById('image_attention')!.classList.add('d-none');
            }
          });
        }
      };
    }
    // 連続して同じファイルを選択できるように選択済みのものをクリア
    (event.target as HTMLInputElement).value = '';
    document.getElementById('image_attention')!.classList.remove('d-none');
  }

  createHiddenTag(name, value) {
    const hiddenTag = document.createElement('input');
    hiddenTag.type = 'hidden';
    hiddenTag.name = name;
    hiddenTag.value = value;
    return hiddenTag
  }

  // ファイルの読み込み
  async compressFile(file, fileReader) {
    const compressedFile = await imageCompression(file, { maxSizeMB: 1 });
    fileReader.readAsDataURL(compressedFile);
  }
}
